import { useCallback, useState } from 'react'
import { Progress } from '../Progress'
import { CircularProgress, IconButton } from '@mui/material'
import * as Yup from 'yup'
import { useClaimRequest } from '../../api/getClaimRequest'
import { defaultImage, TERMINOLOGY } from 'utils/enum'
import { CardDetail } from '../CardDetail'
import { EvaluationStatus, IncidentEnums, Mitigation_Status } from '../enums'
import { ProgressId, downloadPdfDocument, convertFirstLetterToUpperCase } from 'utils/function'
import { Box, Flex } from 'components/elements'
import { LeftArrowIcon } from 'components/icons'
import { DetailedCard, InfoColumn, InlineTextCss, MainWrapCSS, ProgressCard, SideCard, SummaryCard, ToggleButtonCss, flexCss } from '../style'
import { Button } from 'components/button'
import { flexWrapCss } from './IncidentsTable'
import { IncidentDetailsProps } from '../type'
import EvaluationReq from '../EvaluationReq'

export const reEvaluationSchema = Yup.object().shape({
  description: Yup.string().min(10, 'Description is required').required('Description is required'),
  documentFile: Yup.mixed()
    .test('isFilePresent', 'No file is selected', value => {
      const hasNonEmptyValues = value.some((item: string) => item === '')
      if (hasNonEmptyValues) return true
      const fileList = value[0]
      return fileList && fileList[0]
    })
    .test('fileType', 'File type is not supported', value => {
      const hasNonEmptyValues = value.some((item: string) => item === '')
      if (hasNonEmptyValues) return true
      const fileList = value[0]
      const allowedTypes = ['image/jpeg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/pdf']
      return fileList[0] && allowedTypes.includes(fileList[0].type)
    })
    .test('fileSize', 'File size should not exceed 10mb', value => {
      const hasNonEmptyValues = value.some((item: string) => item === '')
      if (hasNonEmptyValues) return true
      const fileList = value[0]
      return fileList[0] && fileList[0].size <= 10 * 1024 * 1024
    }),
})
const IncidentsDetails = ({ row, onExpand, isDesktopOrLaptop, isMobileOrTablet, isPrimaryService }: IncidentDetailsProps) => {
  const [showCaseHistory, setShowCaseHistory] = useState<boolean>(false)
  const [showMore, setShowMore] = useState<boolean>(false)
  const [isChainTransactionLoading, setIsChainTransactionLoading] = useState(false)
  const [loadingReport, setLoadingReport] = useState(true)
  const [isMISP, setIsMISP] = useState<boolean>(false)
  const [downloadStatus, setDownloadStatus] = useState<boolean | null>(null)

  const [showSummary, setShowSummary] = useState<boolean>(true)

  const { data: claimRequest, isFetched: isClaimAPIFetched } = useClaimRequest({ threatId: row?.threatId })

  const summaryDetail = row?.insuranceDetail.summaryDetail || []
  const claimRequestData = claimRequest?.data?.claimRequest || []
  const threatId = row?.threatId || 0
  const threatStatus = row?.threatStatus || []
  const claimRequestFilter = claimRequestData.filter((items: { threatId: number }) => items.threatId === threatId)

  const claimRequestAmount = claimRequestFilter[0]?.claimAmount
  const claimRequestId = claimRequestData[0]?.id || 0

  const ThreadInfo = row[IncidentEnums.THREAT_INFO]
  const evaluationStatus = EvaluationStatus.EDR

  const handleDownloadPdfDocument = useCallback(() => {
    setShowMore(true)
    setIsMISP(true)
    setShowCaseHistory(true)
    setDownloadStatus(false)
    const firstTwoElements = document.createElement('div')
    firstTwoElements.style.width = '1200px'
    firstTwoElements.style.position = 'absolute'
    firstTwoElements.style.left = '-9999px'
    firstTwoElements.style.top = '-9999px'
    firstTwoElements.style.zIndex = '-1'

    firstTwoElements.id = 'tempContainer'
    const divToDownload1a = document.getElementById('divToDownload1a')
    const divToDownload1 = document.getElementById('divToDownload1')

    if (divToDownload1a && divToDownload1) {
      firstTwoElements.appendChild(divToDownload1a.cloneNode(true))
      const clonedDiv = divToDownload1.cloneNode(true) as HTMLElement
      clonedDiv.style.padding = '2rem 0 0'
      firstTwoElements.appendChild(clonedDiv)
      divToDownload1a.appendChild(firstTwoElements)
    }
    const divToDownload4 = document.getElementById('divToDownload4')

    const pdfElements = [firstTwoElements, document.getElementById('divToDownload2'), document.getElementById('divToDownload3'), row?.mispData ? divToDownload4 : null].filter(
      (element): element is HTMLElement => element !== null
    )

    downloadPdfDocument(
      pdfElements,
      () => {
        setShowMore(false)
        setIsMISP(false)
        setShowCaseHistory(false)

        const tempDiv = document.getElementById('tempContainer')
        if (tempDiv) {
          tempDiv.remove()
        }
      },
      setDownloadStatus
    )
  }, [row?.mispData])

  const openFullscreen = (id: string) => {
    document.getElementById(id)?.requestFullscreen()
  }

  return (
    <>
      <Box className="detail-box" style={{ padding: 0 }}>
        {!isDesktopOrLaptop && (
          <IconButton onClick={onExpand} className="back--button" id="incident_details_button">
            <LeftArrowIcon />
            <Box as="p" css={{ margin: '0 0 0 1.5rem' }}>
              Incident Details
            </Box>
          </IconButton>
        )}
        {!isMobileOrTablet && (
          <Flex css={ToggleButtonCss}>
            <Button transparent={!showSummary} type="button" size="sm" onClick={() => setShowSummary(true)} id="threat_details_button">
              Threat Details
            </Button>
            <Button transparent={showSummary} type="button" size="sm" onClick={() => setShowSummary(false)} id="carrier_details_button">
              {TERMINOLOGY.partner.singular}
            </Button>
          </Flex>
        )}
        <>
          <ProgressCard id="divToDownload1a">
            <Progress id={ProgressId(threatStatus)} />
          </ProgressCard>
          <Flex justify="between" wrap="wrap" css={MainWrapCSS}>
            <Box css={flexCss}>
              {(showSummary || isMobileOrTablet) && (
                <SummaryCard>
                  <Box id="divToDownload1">
                    <Box css={flexCss} style={{ marginBottom: '1.5rem' }}>
                      <Box as="h3" css={{ mb: 0 }}>
                        Threat Summary
                      </Box>
                      {
                        <Button
                          disabled={isChainTransactionLoading || loadingReport}
                          onClick={handleDownloadPdfDocument}
                          data-html2canvas-ignore="true"
                          id="download_report_button"
                        >
                          {downloadStatus === null ? (
                            'Download Report'
                          ) : downloadStatus === false ? (
                            <CircularProgress size={18} sx={{ color: 'var(--colors-white) !important' }} />
                          ) : (
                            'Download Report'
                          )}
                        </Button>
                      }
                    </Box>
                    <InfoColumn css={{ mb: '$6' }}>
                      <Box css={flexWrapCss}>
                        {summaryDetail.length &&
                          summaryDetail.map((detail: object, key: number) => {
                            return <CardDetail key={key} data={detail} />
                          })}
                      </Box>
                      <DetailedCard css={{ maxWidth: 'none' }}>
                        <Box as="h3">Claim Summary</Box>
                        <Box as="p" css={{ wordWrap: 'break-word' }}>
                          On {new Date(summaryDetail.createdAt).toDateString()}, internal threat monitoring for the company acknowledged an incident of {summaryDetail.incidentName}{' '}
                          detected on {summaryDetail.attackType} compromise in {summaryDetail.filePath}.
                        </Box>
                        <Box as="h4" css={InlineTextCss} style={{ marginBottom: 8 }}>
                          Attack type:{' '}
                          <Box as="h4" style={{ fontWeight: 'normal' }}>
                            {ThreadInfo?.Classification}
                          </Box>
                        </Box>
                        <Box as="h4" css={InlineTextCss} style={{ marginBottom: 8 }}>
                          Mitigation status:{' '}
                          <Box as="h4" style={{ fontWeight: 'normal' }}>
                            {summaryDetail?.mitigationStatus === Mitigation_Status.MITIGATED
                              ? convertFirstLetterToUpperCase(summaryDetail?.mitigationStatus)
                              : Mitigation_Status.NOT_MITIGATED}
                          </Box>
                        </Box>
                        <Box as="h3" className="heading">
                          Actions taken on threat:
                        </Box>
                        <Box as="h4" css={InlineTextCss} style={{ marginBottom: 8 }}>
                          Threat Quarantined Status:{' '}
                          <Box as="h4" css={{ fontWeight: 'normal', color: '$primary', mb: 0 }}>
                            {summaryDetail.quarantineInfo.quarantineStatus}
                          </Box>
                        </Box>
                        {summaryDetail.quarantineInfo.quarantineStatus === 'success' && (
                          <>
                            <Box as="h4" css={InlineTextCss} style={{ marginBottom: 8 }}>
                              Quarantine - Start time:{' '}
                              <Box as="h4" css={{ fontWeight: 'normal', mb: 0 }}>
                                {new Date(summaryDetail.quarantineInfo.quarantineStartTime).toDateString()}
                              </Box>
                            </Box>
                            <Box as="h4" css={InlineTextCss} style={{ marginBottom: 8 }}>
                              Quarantine - End time:{' '}
                              <Box as="h4" style={{ fontWeight: 'normal' }}>
                                {new Date(summaryDetail.quarantineInfo.quarantineEndTime).toDateString()}
                              </Box>
                            </Box>
                          </>
                        )}
                        <Box as="h4" css={InlineTextCss} style={{ marginBottom: 8 }}>
                          Threat killing Status:{' '}
                          <Box as="h4" css={{ fontWeight: 'normal', color: '$primary', mb: 0 }}>
                            {summaryDetail.threatKillingInfo.killedStatus}
                          </Box>
                        </Box>
                        {summaryDetail.threatKillingInfo.killedStatus === 'success' && (
                          <>
                            <Box as="h4" css={InlineTextCss} style={{ marginBottom: 8 }}>
                              Threat killing - Start time:{' '}
                              <Box as="h4" css={{ fontWeight: 'normal', mb: 0 }}>
                                {new Date(summaryDetail.threatKillingInfo.threatKillingStartTime).toDateString()}
                              </Box>
                            </Box>
                            <Box as="h4" css={InlineTextCss} style={{ marginBottom: 8 }}>
                              Threat killing - End time:{' '}
                              <Box as="h4" style={{ fontWeight: 'normal' }}>
                                {new Date(summaryDetail.threatKillingInfo.threatKillingEndTime).toDateString()}
                              </Box>
                            </Box>
                          </>
                        )}
                      </DetailedCard>
                    </InfoColumn>
                  </Box>
                  {isPrimaryService && (
                    <EvaluationReq
                      showCaseHistory={showCaseHistory}
                      showMore={showMore}
                      isMISP={isMISP}
                      row={row}
                      claimRequestId={claimRequestId}
                      loadingReport={loadingReport}
                      setLoadingReport={setLoadingReport}
                      setIsChainTransactionLoading={setIsChainTransactionLoading}
                      claimRequestAmount={claimRequestAmount}
                      setShowCaseHistory={setShowCaseHistory}
                      setShowMore={setShowMore}
                      setIsMISP={setIsMISP}
                      evaluationStatus={evaluationStatus}
                      isPrimaryService={isPrimaryService}
                      threatStatus={threatStatus}
                      isClaimAPIFetched={isClaimAPIFetched}
                      onExpand={onExpand}
                    />
                  )}
                </SummaryCard>
              )}
              {(!showSummary || isMobileOrTablet) && (
                <>
                  <SideCard>
                    <h3 className="title">{TERMINOLOGY.partner.singular}</h3>
                    <Flex wrap="wrap" direction="column">
                      <Box as="h4" css={{ mb: '.75rem' }}>
                        {row?.companyName}
                      </Box>
                      <Box
                        as="img"
                        src={row?.companyLogoUrl?.urls || defaultImage.logo}
                        alt="company Image"
                        id="image3"
                        css={{ borderRadius: '0.5rem', width: '100%' }}
                        onClick={() => openFullscreen('image3')}
                      />
                    </Flex>
                  </SideCard>
                </>
              )}
            </Box>
          </Flex>
        </>
      </Box>
    </>
  )
}

export default IncidentsDetails
